import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { Patient, PatientSearchParams, PatientSearchResult } from '../../types/patients';
import { patientsAPI } from '../../services/api/patientsAPI';

interface PatientsState {
  patients: Patient[];
  currentPatient: Patient | null;
  searchResults: PatientSearchResult | null;
  isLoading: boolean;
  error: string | null;
  filters: PatientSearchParams;
}

const initialState: PatientsState = {
  patients: [],
  currentPatient: null,
  searchResults: null,
  isLoading: false,
  error: null,
  filters: {
    limit: 20,
    offset: 0,
    sort_by: 'created_at',
    sort_order: 'desc',
  },
};

export const searchPatients = createAsyncThunk(
  'patients/search',
  async (params: PatientSearchParams, { rejectWithValue }) => {
    try {
      const result = await patientsAPI.searchPatients(params);
      return result;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Search failed');
    }
  }
);

export const getPatient = createAsyncThunk(
  'patients/getPatient',
  async (patientId: string, { rejectWithValue }) => {
    try {
      const patient = await patientsAPI.getPatient(patientId);
      return patient;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to get patient');
    }
  }
);

const patientsSlice = createSlice({
  name: 'patients',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentPatient: (state, action: PayloadAction<Patient | null>) => {
      state.currentPatient = action.payload;
    },
    setFilters: (state, action: PayloadAction<PatientSearchParams>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSearchResults: (state) => {
      state.searchResults = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Search patients
      .addCase(searchPatients.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchPatients.fulfilled, (state, action: PayloadAction<PatientSearchResult>) => {
        state.isLoading = false;
        state.searchResults = action.payload;
        // If this is a new search (offset 0), replace patients array
        if (state.filters.offset === 0) {
          state.patients = action.payload.patients;
        } else {
          // Otherwise, append to existing patients (pagination)
          state.patients = [...state.patients, ...action.payload.patients];
        }
      })
      .addCase(searchPatients.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Get patient
      .addCase(getPatient.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPatient.fulfilled, (state, action: PayloadAction<Patient>) => {
        state.isLoading = false;
        state.currentPatient = action.payload;
      })
      .addCase(getPatient.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setCurrentPatient,
  setFilters,
  clearSearchResults,
} = patientsSlice.actions;

export default patientsSlice.reducer;
